import {
  formatNumber,
  LineChart,
  formatDatetime,
  Flex,
  DisplaySwitch,
  CurrencyValue
} from '@blockanalitica/ui';
import { useCallback, useMemo, useState } from 'react';
import { useChartPeriod } from '@/hooks';
import { ExtendedChartOptions } from '@blockanalitica/ui/package/types/src/components/charts/Chart';

export default function HistoricTabs({ data, timeFilter, chartDaysAgo }) {
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);

  const displayOptions = [
    { key: 'total', value: 'Total Assets' },
    { key: 'balances', value: 'Tokens' }
  ];
  const config = updateChartConfigWithPeriod({
    options: { fill: false } as ExtendedChartOptions
  });

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  const yScaleValueFormatter = useMemo(
    () => (value: number) => {
      return `${formatNumber(value)}`;
    },
    []
  );
  const [activeDisplayOption, setActiveDisplayOption] = useState('total');

  const datasets = useMemo(() => {
    switch (activeDisplayOption) {
      case 'balances':
        return [
          {
            label: 'USDS Balance',
            borderColor: 'rgba(255, 139, 91, 1)',
            xField: 'date',
            yField: 'usds_balance'
          },
          {
            label: 'sUSDS Balance',
            borderColor: 'rgba(0, 245, 170, 1)',
            xField: 'date',
            yField: 'susds_balance'
          },
          {
            label: 'USDC Balance',
            borderColor: 'rgba(100, 150, 250, 1)',
            xField: 'date',
            yField: 'usdc_balance'
          }
        ];
      default:
        return [
          {
            label: 'Total Assets',
            xField: 'date',
            yField: 'total_balance'
          }
        ];
    }
  }, [activeDisplayOption]);

  const valueFormatter = useMemo(() => {
    return function valueFormatter(value: string) {
      switch (activeDisplayOption) {
        case 'total':
          return <CurrencyValue value={value} options={{ currency: 'USD' }} />;
        default:
          return <CurrencyValue value={value} options={{ currency: '' }} />;
      }
    };
  }, [activeDisplayOption]);

  return (
    <Flex flexDirection="column" gap="xs">
      <DisplaySwitch
        data={displayOptions}
        activeOption={activeDisplayOption}
        onChange={setActiveDisplayOption}
      />
      <LineChart
        key={activeDisplayOption}
        data={data}
        datasets={datasets}
        valueFormatter={valueFormatter}
        yScaleValueFormatter={yScaleValueFormatter}
        timeFilter={timeFilter}
        config={config}
        subValueFormatter={subValueFormatter}
      />
    </Flex>
  );
}
